import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Select,
  SelectItem,
  Image,
  Chip,
} from '@nextui-org/react';
import {
  BarChart3,
  Trophy,
  Award,
  Medal,
  AlertTriangle,
  RefreshCw,
  Info,
} from 'lucide-react';
import proRankingService, {
  ProRanking,
  ProRankingEntry,
  ProRankingEntriesResponse,
} from '../services/proRankingService';

const TEAL = '#3FCED2';
const PAGE_SIZE = 100;

const RankingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();

  const [rankings, setRankings] = useState<ProRanking[]>([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [rankingsError, setRankingsError] = useState<string | null>(null);

  const [selectedRankingId, setSelectedRankingId] = useState<string | null>(null);
  const [entriesResponse, setEntriesResponse] = useState<ProRankingEntriesResponse | null>(null);
  const [allEntries, setAllEntries] = useState<ProRankingEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const selectedRanking = useMemo(
    () => rankings.find((r) => (r.id || r._id) === selectedRankingId) || null,
    [rankings, selectedRankingId],
  );

  const fetchRankings = useCallback(async () => {
    try {
      setLoadingRankings(true);
      setRankingsError(null);
      const data = await proRankingService.getAllRankings();
      const sorted = [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setRankings(sorted);
    } catch (err) {
      console.error('Fehler beim Laden der Ranglisten:', err);
      setRankingsError('Ranglisten konnten nicht geladen werden');
    } finally {
      setLoadingRankings(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const selectRanking = useCallback(async (rankingId: string, updateUrl: boolean = true) => {
    setSelectedRankingId(rankingId);
    setLoadingEntries(true);
    setAllEntries([]);
    setEntriesResponse(null);

    try {
      const response = await proRankingService.getRankingEntries(rankingId, PAGE_SIZE, 0);
      setEntriesResponse(response);
      setAllEntries(response.entries);

      if (updateUrl) {
        const ranking = rankings.find((r) => (r.id || r._id) === rankingId);
        if (ranking?.slug) {
          window.history.replaceState(null, '', `/ranglisten/${ranking.slug}`);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Einträge:', err);
    } finally {
      setLoadingEntries(false);
    }
  }, [rankings]);

  // Initial selection (by slug if available, else first ranking)
  useEffect(() => {
    if (rankings.length === 0 || selectedRankingId) return;

    if (slug) {
      const found = rankings.find((r) => r.slug === slug);
      if (found && (found.id || found._id)) {
        selectRanking((found.id || found._id) as string, false);
        return;
      }
    }

    const first = rankings[0];
    const firstId = first.id || first._id;
    if (firstId) selectRanking(firstId, true);
  }, [rankings, slug, selectedRankingId, selectRanking]);

  const loadMoreEntries = useCallback(async () => {
    if (loadingMore || !selectedRankingId) return;
    if (!entriesResponse?.pagination?.hasMore) return;

    setLoadingMore(true);
    try {
      const response = await proRankingService.getRankingEntries(
        selectedRankingId,
        PAGE_SIZE,
        allEntries.length,
      );
      setAllEntries((prev) => [...prev, ...response.entries]);
      setEntriesResponse((prev) =>
        prev
          ? { ...prev, pagination: response.pagination, ranking: response.ranking }
          : response,
      );
    } catch (err) {
      console.error('Fehler beim Nachladen:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [allEntries.length, entriesResponse, loadingMore, selectedRankingId]);

  const remainingEntries = useMemo(() => {
    const total = entriesResponse?.pagination?.total ?? 0;
    return Math.max(0, total - allEntries.length);
  }, [entriesResponse, allEntries.length]);

  // ---------- Loading / Error states ----------
  if (loadingRankings) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (rankingsError) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertTriangle size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 mb-4">{rankingsError}</p>
          <Button
            color="primary"
            startContent={<RefreshCw size={16} />}
            onPress={fetchRankings}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <BarChart3 size={64} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">Keine Ranglisten verfügbar</h3>
          <p className="text-gray-500">Es wurden noch keine Ranglisten konfiguriert.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Ranglisten | DartCloud</title>
        <meta
          name="description"
          content="Aktuelle Dart-Ranglisten auf DartCloud. PDC Order of Merit, Weltranglisten und weitere Verbands-Rankings."
        />
        <link rel="canonical" href="https://dartcloudapp.de/ranglisten" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <BarChart3 size={40} style={{ color: TEAL }} />
            Ranglisten
          </h1>
          <p className="text-gray-400">
            Aktuelle Profi-Ranglisten der PDC, WDF und weiterer Organisationen
          </p>
        </div>

        {/* Mobile: Ranking selector */}
        <div className="block lg:hidden mb-6">
          <Select
            label="Rangliste"
            placeholder="Rangliste wählen"
            selectedKeys={selectedRankingId ? [selectedRankingId] : []}
            onChange={(e) => {
              const id = e.target.value;
              if (id) selectRanking(id);
            }}
            classNames={{
              trigger: 'bg-zinc-900 border-zinc-800',
              listbox: 'bg-zinc-900',
            }}
          >
            {rankings.map((r) => {
              const id = (r.id || r._id) as string;
              return (
                <SelectItem key={id} value={id}>
                  {r.name || r.shortName || 'Rangliste'}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main content - left (desktop) */}
          <div className="lg:col-span-9 order-2 lg:order-1">
            {selectedRanking && (
              <SelectedRankingHeader ranking={selectedRanking} />
            )}

            {loadingEntries ? (
              <div className="flex justify-center items-center h-64">
                <Spinner size="lg" style={{ color: TEAL }} />
              </div>
            ) : (
              <EntriesTable
                ranking={entriesResponse?.ranking || null}
                entries={allEntries}
                onLoadMore={loadMoreEntries}
                hasMore={entriesResponse?.pagination?.hasMore ?? false}
                loadingMore={loadingMore}
                remaining={remainingEntries}
              />
            )}
          </div>

          {/* Sidebar - right (desktop) */}
          <div className="hidden lg:block lg:col-span-3 order-1 lg:order-2">
            <Card className="bg-zinc-900 border-zinc-800 sticky top-24">
              <CardBody className="p-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                    Alle Ranglisten
                  </h3>
                </div>
                <ul className="divide-y divide-zinc-800 max-h-[70vh] overflow-y-auto">
                  {rankings.map((r) => {
                    const id = (r.id || r._id) as string;
                    const isSelected = id === selectedRankingId;
                    return (
                      <li key={id}>
                        <button
                          onClick={() => selectRanking(id)}
                          className={`w-full text-left px-4 py-3 transition-colors ${
                            isSelected ? 'bg-zinc-800/80' : 'hover:bg-zinc-800/40'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-1 h-8 rounded-full"
                              style={{ backgroundColor: isSelected ? TEAL : 'transparent' }}
                            />
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm font-medium line-clamp-1 ${
                                  isSelected ? 'text-white' : 'text-gray-300'
                                }`}
                              >
                                {r.name || r.shortName || 'Rangliste'}
                              </p>
                              {r.organization && (
                                <p className="text-xs text-gray-500 line-clamp-1">
                                  {r.organization}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Selected Ranking Header ----------
interface SelectedRankingHeaderProps {
  ranking: ProRanking;
}

const SelectedRankingHeader: React.FC<SelectedRankingHeaderProps> = ({ ranking }) => {
  const imageUrl = proRankingService.buildFileUrl(ranking.image || null);
  return (
    <Card className="bg-zinc-900 border-zinc-800 mb-4">
      <CardBody className="p-4">
        <div className="flex items-start gap-4">
          {imageUrl && (
            <Image
              removeWrapper
              src={imageUrl}
              alt={ranking.name || ''}
              className="w-16 h-16 object-contain rounded-lg bg-zinc-800 p-1 flex-shrink-0"
              fallbackSrc="/icon.png"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-xl md:text-2xl font-bold">
                {ranking.name || ranking.shortName || 'Rangliste'}
              </h2>
              {ranking.organization && (
                <Chip
                  size="sm"
                  variant="flat"
                  style={{ backgroundColor: `${TEAL}33`, color: TEAL }}
                >
                  {ranking.organization}
                </Chip>
              )}
            </div>
            {ranking.description && (
              <p className="text-sm text-gray-400 line-clamp-2 mb-1">
                {ranking.description}
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
              {ranking.dataAsOf && <span>Stand: {ranking.dataAsOf}</span>}
              {typeof ranking.entryCount === 'number' && (
                <span>{ranking.entryCount} Einträge</span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

// ---------- Entries Table ----------
interface EntriesTableProps {
  ranking: ProRankingEntriesResponse['ranking'] | null;
  entries: ProRankingEntry[];
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  remaining: number;
}

const EntriesTable: React.FC<EntriesTableProps> = ({
  ranking,
  entries,
  onLoadMore,
  hasMore,
  loadingMore,
  remaining,
}) => {
  const valueColumnHeader = ranking?.valueLabel || 'Wert';
  const multiplierHint = ranking?.valueMultiplierHint;

  if (entries.length === 0) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardBody className="p-12 text-center">
          <BarChart3 size={48} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Keine Einträge in dieser Rangliste</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-0">
        {multiplierHint && (
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/60 flex items-start gap-2">
            <Info size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-400">{multiplierHint}</p>
          </div>
        )}

        {/* Header row */}
        <div className="grid grid-cols-12 px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-wide text-gray-500 font-semibold">
          <div className="col-span-2 md:col-span-1">Rang</div>
          <div className="col-span-6 md:col-span-8">Spieler</div>
          <div className="col-span-4 md:col-span-3 text-right">{valueColumnHeader}</div>
        </div>

        {/* Rows */}
        <ul className="divide-y divide-zinc-800">
          {entries.map((entry, idx) => (
            <EntryRow key={entry.id || entry._id || idx} entry={entry} ranking={ranking} />
          ))}
        </ul>

        {hasMore && (
          <div className="p-4 border-t border-zinc-800 flex justify-center">
            <Button
              onPress={onLoadMore}
              isDisabled={loadingMore}
              isLoading={loadingMore}
              variant="flat"
              className="bg-zinc-800 text-white"
            >
              {loadingMore
                ? 'Lädt...'
                : `Weitere ${Math.min(PAGE_SIZE, remaining)} Spieler laden`}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ---------- Entry Row ----------
interface EntryRowProps {
  entry: ProRankingEntry;
  ranking: ProRankingEntriesResponse['ranking'] | null;
}

const EntryRow: React.FC<EntryRowProps> = ({ entry, ranking }) => {
  const rank = entry.rank ?? 0;
  const isTop10 = rank > 0 && rank <= 10;
  const rowBg = isTop10
    ? rank <= 3
      ? 'bg-[#3FCED2]/[0.06]'
      : 'bg-[#3FCED2]/[0.03]'
    : '';

  return (
    <li className={`grid grid-cols-12 px-4 py-3 items-center ${rowBg}`}>
      <div className="col-span-2 md:col-span-1">
        <RankBadge rank={rank} />
      </div>
      <div className="col-span-6 md:col-span-8 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {entry.country && (
            <span
              className="inline-block w-5 text-center text-[10px] font-bold uppercase text-gray-400 flex-shrink-0"
              title={entry.country}
            >
              {entry.country}
            </span>
          )}
          <span
            className={`truncate ${
              isTop10 ? 'font-semibold text-white' : 'text-gray-200'
            }`}
          >
            {entry.playerName || 'Unbekannt'}
          </span>
        </div>
      </div>
      <div className="col-span-4 md:col-span-3 text-right tabular-nums">
        <span className={isTop10 ? 'font-semibold text-white' : 'text-gray-300'}>
          {proRankingService.formatValue(entry, ranking)}
        </span>
      </div>
    </li>
  );
};

// ---------- Rank Badge ----------
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="flex items-center gap-1">
        <Trophy size={16} style={{ color: '#FFD700' }} />
        <span className="text-sm font-bold" style={{ color: '#FFD700' }}>
          {rank}
        </span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex items-center gap-1">
        <Award size={16} style={{ color: '#C0C0C0' }} />
        <span className="text-sm font-bold" style={{ color: '#C0C0C0' }}>
          {rank}
        </span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex items-center gap-1">
        <Medal size={16} style={{ color: '#CD7F32' }} />
        <span className="text-sm font-bold" style={{ color: '#CD7F32' }}>
          {rank}
        </span>
      </div>
    );
  }
  return <span className="text-sm text-gray-400 font-medium">{rank || '-'}</span>;
};

export default RankingsPage;
